import React from 'react';
import PropTypes from 'prop-types';
import moment, { momentPropTypes } from '@g4/moment-utc';

import Box from '@g4/boneyard/lib/Box';
import styled, { withTheme } from 'styled-components';
import { Spinner, Text } from '../../../../../components/Boneyard';
import Price from '@g4/next-furniture/Price/Price';
import {
  DAY_TYPE_SELECTED_DEPARTURE_DATE,
  DAY_TYPE_SELECTED_RETURN_DATE,
  DAY_TYPE_SELECTED_DEPARTURE_AND_RETURN_DATE,
  DAY_TYPE_RAIDERS_GAME_DAY,
  DAY_TYPE_AFTER_RAIDERS_GAME_DATE,
  DAY_TYPE_BEFORE_RAIDERS_GAME_DATE,
} from '@g4/next-furniture/Calendar/constants/constants';
import { FLIGHT_AVAILABLE, FLIGHT_UNAVAILABLE } from '../../../constants';
import { useTranslations } from '../../../../../graphql/components/TranslationsProvider';
import { useBookingPath } from '../../../../../graphql';
import {
  CALENDAR_DAY_CONTENT,
  CALENDAR_DAY_CONTENT_PRICE,
  CALENDAR_DAY_CONTENT_NO_FLIGHTS,
  CALENDAR_DAY_CONTENT_RAIDERS_GAME_DAY,
} from '../../../../../../automationHooks.json';
import { withValue } from '@g4/automation-hooks';
import { useMedia } from '@g4/boneyard/lib/Media';

const StyledBox = styled(Box)`
  white-space: wrap;
  margin: 0 2.5px 0 2.5px;
  flex-basis: auto;
  height: 100%;
  .prices {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    position: absolute;
    bottom: 5px;
    right: 5px;

    > span {
      &:last-of-type {
        margin-left: ${props => props.theme.spacing(1)};
      }

      del {
        /* stylelint-disable-next-line declaration-no-important */
        position: relative;
        display: inline-block;
        text-decoration-line: none; /* we must override the native strikethrough line and add our custom one */

        &:after {
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          right: 0;
          @media screen and (min-width: 768px) {
            border-top: 2px solid;
          }
          @media screen and (max-width: 767px) {
            border-top: 1px solid;
          }
          border-color: inherit;
          transform: rotate(-12deg);
        }
      }
    }
  }
`;

const propTypes = {
  isLoading: PropTypes.bool,
  date: momentPropTypes.utc.isRequired,
  type: PropTypes.string,
  typeSecondary: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  fare: PropTypes.object,
  theme: PropTypes.object,
};

const DayContent = props => {
  const isLoading = props.isLoading;
  const noFlights = !props.fare;
  const translations = useTranslations();
  const mq = useMedia();
  const {
    calendarDesktopDepartDateLabel,
    calendarMobileDepartDateLabel,
    calendarDesktopReturnDateLabel,
    calendarMobileReturnDateLabel,
    calendarDesktopDepartAndReturnDateLabel,
    calendarMobileDepartAndReturnDateLabel,
  } = translations.flightsPage;

  const { gameDayLabel } = translations.stadiumPackages;
  const { isStadium } = useBookingPath();

  const dayLabelText = {
    [DAY_TYPE_SELECTED_DEPARTURE_DATE]: {
      mobile: calendarMobileDepartDateLabel,
      desktop: calendarDesktopDepartDateLabel,
    },
    [DAY_TYPE_SELECTED_RETURN_DATE]: {
      mobile: calendarMobileReturnDateLabel,
      desktop: calendarDesktopReturnDateLabel,
    },
    [DAY_TYPE_SELECTED_DEPARTURE_AND_RETURN_DATE]: {
      mobile: calendarMobileDepartAndReturnDateLabel,
      desktop: calendarDesktopDepartAndReturnDateLabel,
    },
  };

  const departingOrReturningLabels = dayLabelText[props.typeSecondary];

  const displayDepartingOrReturningLabel =
    props.fare && departingOrReturningLabels;

  const isDesktop = mq.gte('lg');
  const isTablet = mq.gt('sm') && mq.lt('lg');
  const isMobile = mq.gte('xs') && mq.lte('sm');
  const isXsMobile = mq.lte('xs');

  const departingOrReturningLabel = mq.lte('sm')
    ? departingOrReturningLabels?.mobile
    : departingOrReturningLabels?.desktop;

  return (
    <div>
      <div
        style={{
          position: 'relative',
          paddingBottom: '100%',
          height: mq.gt('sm') ? 90 : 65,
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
        >
          {displayDepartingOrReturningLabel && (
            <Box
              d={'flex'}
              direction={'row'}
              justifyContent={'center'}
              pt={1}
              pb={1}
              backgroundColor={props.theme.colors.flights.flightItem.lightBlue}
              height={'20%'}
              width={'100%'}
            >
              <Text
                size={isXsMobile ? 8 : isMobile ? 9 : isTablet ? 10 : 12}
                color={'#ffffff'}
                weight={200}
              >
                {departingOrReturningLabel}
              </Text>
            </Box>
          )}
          <StyledBox
            word-wrap={'break-word'}
            pt={isDesktop ? 2 : 1}
            pr={isDesktop ? 2 : 1}
            pl={isDesktop ? 2 : 1}
            flex={'1'}
          >
            <Box
              d={'flex'}
              h={'30%'}
              direction={'row'}
              justifyContent={mq.gte('md') ? 'space-between' : 'center'}
            >
              <>
                {mq.gte('md') ? (
                  <>
                    <Text
                      size={isMobile ? 12 : isTablet ? 14 : 16}
                      color={props.theme.colors.flights.flightItem.lightBlue}
                      weight={500}
                      automationHook={withValue(
                        CALENDAR_DAY_CONTENT.hook,
                        'weekday'
                      )}
                    >
                      {moment(props.date).format('ddd')}
                    </Text>
                    <Text
                      size={isMobile ? 12 : isTablet ? 14 : 16}
                      color={props.theme.colors.flights.flightItem.lightBlue}
                      weight={500}
                      automationHook={withValue(
                        CALENDAR_DAY_CONTENT.hook,
                        'dateday'
                      )}
                    >
                      {moment(props.date).format('DD')}
                    </Text>
                  </>
                ) : (
                  <Text
                    size={isMobile ? 12 : isTablet ? 14 : 16}
                    color={props.theme.colors.flights.flightItem.lightBlue}
                    weight={500}
                    automationHook={withValue(
                      CALENDAR_DAY_CONTENT.hook,
                      'selected-dateday'
                    )}
                  >
                    {!displayDepartingOrReturningLabel
                      ? moment(props.date).format('DD')
                      : null}
                  </Text>
                )}
              </>
            </Box>
            <Box
              d={'flex'}
              justifyContent={mq.gte('md') ? 'flex-end' : 'center'}
              h={'70%'}
              className={(!isLoading || noFlights) && 'prices'}
              direction={'column'}
            >
              {noFlights ? (
                <Text
                  flex-wrap={'wrap'}
                  size={isXsMobile ? 8 : isMobile ? 10 : isTablet ? 14 : 18}
                  color={props.theme.colors.flights.flightItem.lightBlue}
                  weight={500}
                  automationHook={
                    props.type === DAY_TYPE_RAIDERS_GAME_DAY
                      ? CALENDAR_DAY_CONTENT_RAIDERS_GAME_DAY.hook
                      : CALENDAR_DAY_CONTENT_NO_FLIGHTS.hook
                  }
                >
                  {props.type === DAY_TYPE_RAIDERS_GAME_DAY
                    ? gameDayLabel
                    : 'No Flights'}
                </Text>
              ) : isLoading ? (
                <Spinner
                  className="day-content-spinner"
                  size="xsmall"
                  appearance="dark"
                  automationHook={withValue('day-content', 'spinner')}
                />
              ) : (
                props.type !== DAY_TYPE_RAIDERS_GAME_DAY && (
                  <span>
                    {isStadium ? (
                      <Text
                        flex-wrap={'wrap'}
                        size={
                          isXsMobile ? 8 : isMobile ? 10 : isTablet ? 14 : 18
                        }
                        color={props.theme.colors.flights.flightItem.lightBlue}
                        weight={600}
                        automationHook={
                          CALENDAR_DAY_CONTENT_RAIDERS_GAME_DAY.hook
                        }
                      >
                        {props.type === DAY_TYPE_AFTER_RAIDERS_GAME_DATE ||
                        props.type === DAY_TYPE_BEFORE_RAIDERS_GAME_DATE
                          ? FLIGHT_UNAVAILABLE
                          : FLIGHT_AVAILABLE}
                      </Text>
                    ) : props.fare.strikethrough ? (
                      <del
                        aria-label={
                          'original price: ' + props.fare.strikethrough
                        }
                      >
                        <Price
                          automationHook={withValue(
                            CALENDAR_DAY_CONTENT_PRICE.hook,
                            'original'
                          )}
                          value={props.fare.strikethrough}
                          currency={'$'}
                          currencyFontSize={
                            isXsMobile ? 4 : isMobile ? 6 : isTablet ? 10 : 13
                          }
                          currencyFontWeight={600}
                          decimalFontSize={
                            isXsMobile ? 6 : isMobile ? 8 : isTablet ? 10 : 13
                          }
                          decimalFontWeight={500}
                          priceFontSize={
                            isXsMobile ? 8 : isMobile ? 10 : isTablet ? 12 : 24
                          }
                          priceFontWeight={500}
                          color={
                            props.theme.colors.flights.flightItem.lightBlue
                          }
                        />
                      </del>
                    ) : null}
                  </span>
                )
              )}
              {!noFlights &&
                !isLoading &&
                (props.type === DAY_TYPE_RAIDERS_GAME_DAY ? (
                  <Text
                    flex-wrap={'wrap'}
                    size={isXsMobile ? 8 : isMobile ? 10 : isTablet ? 14 : 18}
                    color={props.theme.colors.flights.flightItem.lightBlue}
                    weight={500}
                    automationHook={CALENDAR_DAY_CONTENT_RAIDERS_GAME_DAY.hook}
                  >
                    {gameDayLabel}
                  </Text>
                ) : (
                  <span
                    aria-label={'discounted price: ' + props.fare.salesTotal}
                  >
                    {!isStadium && (
                      <Price
                        currencyFontSize={
                          isXsMobile ? 8 : isMobile ? 10 : isTablet ? 12 : 18
                        }
                        decimalFontSize={
                          isXsMobile ? 6 : isMobile ? 8 : isTablet ? 12 : 16
                        }
                        priceFontSize={
                          isXsMobile ? 10 : isMobile ? 12 : isTablet ? 15 : 30
                        }
                        value={props.fare.salesTotal}
                        color={props.theme.colors.flights.flightItem.lightBlue}
                        automationHook={withValue(
                          CALENDAR_DAY_CONTENT_PRICE.hook,
                          'discounted'
                        )}
                      />
                    )}
                  </span>
                ))}
            </Box>
          </StyledBox>
        </div>
      </div>
    </div>
  );
};

DayContent.propTypes = propTypes;
DayContent.displayName = 'DayContent';

export default withTheme(DayContent);
